import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ListProDocsWithAuthorResponse,
  ProDocWithAuthor,
  ToggleProDocFavoriteRequest,
} from '@dreamai/types';
import api from '../../api';
import AgentTabs from './AgentTabs';
import ErrorState from '../common/ErrorState';
import ProDocCard from './ProDocCard';
import { ReactComponent as EmptyArchiveIcon } from '../../icons/empty-archive.svg';
import './conversational-dashboard.scss';
import './pro-doc-list.scss';

const ProDocList: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [proDocs, setProDocs] = useState<ProDocWithAuthor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load ProDocs when component mounts
  useEffect(() => {
    const loadProDocs = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        const response = await api.get<ListProDocsWithAuthorResponse>(
          '/pro-docs/with-authors',
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.success) {
          setProDocs(response.data.proDocs);
        } else {
          setError(response.data.error || 'Failed to load ProDocs');
        }
      } catch (err) {
        console.error('Error loading ProDocs:', err);
        setError('Failed to load ProDocs');
      } finally {
        setLoading(false);
      }
    };

    loadProDocs();
  }, [getAccessTokenSilently]);

  const handleTabClick = (agentId: string) => {
    navigate(`/?agent_id=${agentId}`);
  };

  const handleDocClick = (proDocId: string) => {
    navigate(`/docs/${proDocId}`);
  };

  const handleFavoriteToggle = async (
    proDocId: string,
    isFavorited: boolean,
  ) => {
    // Optimistically update the UI
    setProDocs((prevDocs) =>
      prevDocs.map((doc) =>
        doc.id === proDocId ? { ...doc, is_favorited: isFavorited } : doc,
      ),
    );

    try {
      const token = await getAccessTokenSilently();
      const requestBody: ToggleProDocFavoriteRequest = {
        proDocId,
        isFavorited,
      };

      await api.post('/pro-docs/favorite', requestBody, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.error('Error toggling favorite:', err);
      // Revert the optimistic update on error
      setProDocs((prevDocs) =>
        prevDocs.map((doc) =>
          doc.id === proDocId ? { ...doc, is_favorited: !isFavorited } : doc,
        ),
      );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className='conversational-dashboard'>
        <div className='loading-top-bar'>
          <div className='loading-agent-ghost'>
            <div className='loading-agent-circle'></div>
          </div>
          <div className='loading-agent-ghost'>
            <div className='loading-agent-circle'></div>
          </div>
        </div>
        <div className='dashboard-main'>
          <div className='pro-docs-fake'>
            <div className='inner'>
              <div className='top-ghost'></div>
              <div className='second-ghost'>
                <div className='box'></div>
                <div className='next-bit'>
                  <div className='top-grey'></div>
                  <div className='bottom-grey'></div>
                </div>
                <div className='rondel-wrapper'>
                  <div className='rondel'></div>
                </div>
              </div>
              <div className='second-ghost'>
                <div className='box'></div>
                <div className='next-bit'>
                  <div className='top-grey'></div>
                  <div className='bottom-grey'></div>
                </div>
                <div className='rondel-wrapper'>
                  <div className='rondel'></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='conversational-dashboard'>
        <AgentTabs activeAgentId={null} onTabClick={handleTabClick} />
        <div className='dashboard-main'>
          <div className='error-agent-tab-wrapper'>
            <ErrorState
              title='Failed to load documents'
              subtitle={error || 'Please try again later'}
            />
          </div>
        </div>
      </div>
    );
  }

  // Success state
  const favoriteDocs = proDocs.filter((doc) => doc.is_favorited);
  const nonFavoriteDocs = proDocs.filter((doc) => !doc.is_favorited);

  if (favoriteDocs.length! === 0 && nonFavoriteDocs.length === 0) {
    return (
      <div className='conversational-dashboard'>
        <AgentTabs activeAgentId={null} onTabClick={handleTabClick} />
        <div className='dashboard-main'>
          <div className='empty-docs-big'>
            <div className='empty-docs-inner'>
              <EmptyArchiveIcon />
              <div>
                <h3>There aren't any documents here yet</h3>
                <p>
                  You'll be able to review documents and sheets your Pros have
                  created for you here.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='conversational-dashboard'>
      <AgentTabs activeAgentId={null} onTabClick={handleTabClick} />

      <div className='dashboard-main'>
        <div className='pro-docs-list'>
          {/* Favorites section - only show if there are favorites */}
          {favoriteDocs.length > 0 && (
            <div className='docs-section'>
              <h1>Favourites</h1>
              <div className='docs-grid'>
                {favoriteDocs.map((doc) => (
                  <ProDocCard
                    key={doc.id}
                    doc={doc}
                    onClick={handleDocClick}
                    onFavoriteToggle={handleFavoriteToggle}
                  />
                ))}
              </div>
            </div>
          )}

          {/* My documents section */}
          <div className='docs-section'>
            <h1>My documents</h1>
            {nonFavoriteDocs.length === 0 ? (
              <div className='empty-docs-inner'>
                <EmptyArchiveIcon />
                <div>
                  <h3>That's all doc</h3>
                  <p>
                    You'll be able to review documents and sheets your Pros have
                    created for you here.
                  </p>
                </div>
              </div>
            ) : (
              <div className='docs-grid'>
                {nonFavoriteDocs.map((doc) => (
                  <ProDocCard
                    key={doc.id}
                    doc={doc}
                    onClick={handleDocClick}
                    onFavoriteToggle={handleFavoriteToggle}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProDocList;
